import React, { useMemo, useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { createPortal } from 'react-dom';
import { renderToString } from 'react-dom/server';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MapPin, Share2, MessageCircle, Sparkles,
    Store, CalendarDays, Handshake, ShoppingBag, Users,
    Layers, Eye, EyeOff,
} from 'lucide-react';
import clsx from 'clsx';
import { IconButton } from '../ui/IconButton';
import logo from '../../assets/logo.png';

const PIN_PALETTE = {
    business: { bg: '#8B9A6B', icon: <Store size={18} color="white" strokeWidth={2.5} /> },
    event: { bg: '#C9A66B', icon: <CalendarDays size={18} color="white" strokeWidth={2.5} /> },
    barter: { bg: '#7C9885', icon: <Handshake size={18} color="white" strokeWidth={2.5} /> },
    store: { bg: '#6B7E9A', icon: <ShoppingBag size={18} color="white" strokeWidth={2.5} /> },
};

const makePin = (kind) => {
    const p = PIN_PALETTE[kind];
    return L.divIcon({
        className: 'zone-hero-pin',
        html: `
            <div style="
                width: 32px; height: 32px;
                background: ${p.bg};
                border: 2.5px solid white;
                border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                box-shadow: 0 4px 12px rgba(0,0,0,0.25);
            ">${renderToString(p.icon)}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -16],
    });
};

const PINS = {
    business: makePin('business'),
    event: makePin('event'),
    barter: makePin('barter'),
    store: makePin('store'),
};

// Pin for *other* zones — the branded logo disc. Visually distinct from the
// entity pins above so it reads as "another community" rather than a place.
const ZONE_PIN = L.divIcon({
    className: 'zone-hero-zone-pin',
    html: `
        <div style="
            width: 38px; height: 38px;
            border-radius: 50%;
            border: 3px solid #E5B55F;
            box-shadow: 0 3px 10px rgba(229,181,95,0.45);
            background: white;
            overflow: hidden;
            display: flex; align-items: center; justify-content: center;
        ">
            <img src="${logo}" style="width: 100%; height: 100%; object-fit: cover;" />
        </div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -19],
});

const escapeHtml = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

// Branded cluster bubble — sized by how many zones it groups. Same look as the
// main zones map (ZonesMap) so the network reads consistently everywhere.
const createClusterIcon = (cluster) => {
    const count = cluster.getChildCount();
    const size = count < 10 ? 38 : count < 50 ? 46 : 56;
    return L.divIcon({
        className: 'zone-cluster-marker',
        html: `<div style="
            width:${size}px;height:${size}px;border-radius:50%;
            display:flex;align-items:center;justify-content:center;
            background:rgba(229,181,95,0.92);
            border:3px solid #fff;
            box-shadow:0 3px 12px rgba(229,181,95,0.5);
            color:#fff;font-weight:700;
            font-size:${count < 100 ? 15 : 13}px;
            font-family:Rubik, Nunito, Inter, system-ui, sans-serif;
        ">${count}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

// Renders every other zone inside a leaflet.markercluster group, so when the
// user zooms out from their own zone the surrounding zones collapse into tidy
// bubbles instead of overlapping — and each pin links straight to that zone.
const ClusteredZoneMarkers = ({ zones }) => {
    const map = useMap();
    React.useEffect(() => {
        if (!zones || zones.length === 0) return undefined;

        const group = L.markerClusterGroup({
            chunkedLoading: true,
            maxClusterRadius: 55,
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: true,
            iconCreateFunction: createClusterIcon,
        });

        zones.forEach((zone) => {
            if (zone.lat == null || zone.lng == null) return;
            const marker = L.marker([zone.lat, zone.lng], { icon: ZONE_PIN });
            const name = escapeHtml(zone.name);
            const routeId = zone.id || zone.name;
            marker.bindPopup(
                `<div style="font-family:Rubik, Nunito, Inter, Varela Round, system-ui, sans-serif;padding:4px 0">
                    <strong style="font-size:16px;color:#222;margin-bottom:6px;display:block">${name}</strong>
                    <a href="?view=zone-details&zone=${encodeURIComponent(routeId)}"
                       style="color:#E5B55F;text-decoration:none;font-size:14px;font-weight:500;display:inline-flex;align-items:center;gap:4px">
                       לעמוד הקהילה →
                    </a>
                </div>`,
                { className: 'zone-popup' }
            );
            group.addLayer(marker);
        });

        map.addLayer(group);
        return () => { map.removeLayer(group); };
    }, [zones, map]);

    return null;
};

function parseLatLng(raw) {
    if (!raw) return null;
    try {
        const loc = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (loc?.lat && loc?.lng) return [loc.lat, loc.lng];
        if (loc?.latitude && loc?.longitude) return [loc.latitude, loc.longitude];
    } catch { /* noop */ }
    return null;
}

function FitToPoints({ points, zoneCenter, fallbackPoints, onFramed }) {
    const map = useMap();
    // Fires once, the first time we've applied *any* real view. The parent
    // keeps the map hidden under a gradient until then, so the map is only
    // ever revealed already framed on the right place — no wrong-location
    // flash, no mid-interaction snap.
    const framedNotified = React.useRef(false);
    // The zone's own data (entities / center) loads independently from the
    // "other zones" list — and often later. Keep re-evaluating until we've
    // framed THIS zone, so a temporary network-wide fallback view always gets
    // upgraded to the zone once its data arrives. Once we've fit to the zone
    // we stop, so we never yank the map out from under the user afterwards.
    const fittedToZone = React.useRef(false);
    const fallbackApplied = React.useRef(false);
    // Whether any view has already been applied. The first framing is an
    // instant snap (nothing to transition from); upgrading a stopgap/fallback
    // view to the real zone is animated so it glides instead of teleporting.
    const anyApplied = React.useRef(false);
    // The zone's own data usually arrives ~1s after mount. Don't flash the
    // zoomed-out network view in that window — give the zone a grace period to
    // load first, and only fall back to the network if it's still not here.
    const FALLBACK_GRACE_MS = 1200;
    const graceTimer = React.useRef(null);
    const graceElapsed = React.useRef(false);

    React.useEffect(() => {
        if (fittedToZone.current) return;
        const animated = anyApplied.current;
        const flyOpts = { duration: 0.8, easeLinearity: 0.25 };
        const goToBounds = (b, opts) =>
            animated
                ? map.flyToBounds(b, { ...opts, ...flyOpts })
                : map.fitBounds(b, opts);
        const goToView = (latlng, zoom) =>
            animated
                ? map.flyTo(latlng, zoom, flyOpts)
                : map.setView(latlng, zoom);

        // Frame the network as a last resort. Deferred until the grace period
        // elapses so a quick-loading zone never gets overshadowed by it.
        const applyFallback = () => {
            if (fittedToZone.current || fallbackApplied.current) return;
            if (fallbackPoints && fallbackPoints.length > 1) {
                goToBounds(fallbackPoints, { padding: [50, 50], maxZoom: 13 });
                fallbackApplied.current = true;
                anyApplied.current = true;
            } else if (fallbackPoints && fallbackPoints.length === 1) {
                goToView(fallbackPoints[0], 12);
                fallbackApplied.current = true;
                anyApplied.current = true;
            }
        };

        if (zoneCenter) {
            // Frame a blend of the zone's own location and its businesses:
            // include the zone center plus every business within 5 km of it,
            // so the view is as tight as the businesses allow but never spans
            // more than a ~5 km radius around the zone. Businesses farther out
            // are ignored rather than zooming the whole zone out to reach them.
            const MAX_RADIUS_M = 5000;
            const center = L.latLng(zoneCenter);
            const inRadius = (points || []).filter(
                (p) => center.distanceTo(L.latLng(p)) <= MAX_RADIUS_M
            );
            if (inRadius.length > 0) {
                const bounds = L.latLngBounds([zoneCenter, ...inRadius]);
                goToBounds(bounds, { padding: [40, 40], maxZoom: 16 });
            } else {
                // No businesses near the zone — just center on the zone itself.
                goToView(zoneCenter, 15);
            }
            fittedToZone.current = true;
            anyApplied.current = true;
        } else if (points && points.length > 1) {
            // Zone center hasn't loaded yet — frame the entities as a stopgap.
            // Not marked final, so it upgrades to the zone location once it arrives.
            goToBounds(points, { padding: [40, 40], maxZoom: 16 });
            anyApplied.current = true;
        } else if (points && points.length === 1) {
            goToView(points[0], 15);
            anyApplied.current = true;
        } else if (!fallbackApplied.current) {
            // Nothing for this zone yet. If the grace period has already
            // elapsed, show the network now; otherwise wait it out so a
            // soon-to-arrive zone never gets preceded by the network flash.
            if (graceElapsed.current) {
                applyFallback();
            } else if (graceTimer.current === null) {
                graceTimer.current = setTimeout(() => {
                    graceTimer.current = null;
                    graceElapsed.current = true;
                    applyFallback();
                }, FALLBACK_GRACE_MS);
            }
        }

        // Zone data arrived (or fallback ran) — the pending network flash is
        // no longer wanted.
        if ((fittedToZone.current || anyApplied.current) && graceTimer.current !== null) {
            clearTimeout(graceTimer.current);
            graceTimer.current = null;
        }

        // First real view is in place — let the parent crossfade the map in.
        if (anyApplied.current && !framedNotified.current) {
            framedNotified.current = true;
            onFramed && onFramed();
        }
    }, [points, zoneCenter, fallbackPoints, map, onFramed]);

    React.useEffect(() => () => {
        if (graceTimer.current !== null) clearTimeout(graceTimer.current);
    }, []);

    return null;
}

/**
 * ZoneMapHero — the new top-of-page anchor for a zone.
 *
 * Visual: a tall, immersive map with the zone identity floating top-right
 * (RTL leading edge), category filter chips floating bottom, and the action
 * cluster (chat / share) tucked top-left. If we don't have coordinates yet,
 * we render a poetic gradient fallback with the same chrome.
 */
export function ZoneMapHero({
    zoneName,
    memberCount = 0,
    businesses = [],
    events = [],
    barters = [],
    stores = [],
    otherZones = [],
    zoneCenter,
    onShareClick,
    onChatClick,
    onPinClick,
    fullScreen = false,
    loading = false,
}) {
    const [visible, setVisible] = useState({
        business: true,
        event: true,
        barter: true,
        store: true,
    });
    const [layersOpen, setLayersOpen] = useState(false);
    const layersRef = useRef(null);   // trigger anchor
    const popoverRef = useRef(null);  // portalled popover content
    // The popover is portalled to <body> to escape the map's z-0 stacking
    // context (it would otherwise sit under the zone's floating toolbar).
    // Since it's fixed-positioned, anchor it to the trigger's viewport rect.
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (!layersOpen) return;
        const place = () => {
            const r = layersRef.current?.getBoundingClientRect();
            if (r) setPopoverPos({ top: r.bottom + 8, left: r.left });
        };
        place();
        const onDocClick = (e) => {
            const inTrigger = layersRef.current?.contains(e.target);
            const inPopover = popoverRef.current?.contains(e.target);
            if (!inTrigger && !inPopover) setLayersOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setLayersOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [layersOpen]);

    const counts = {
        business: businesses?.length || 0,
        event: events?.length || 0,
        barter: barters?.length || 0,
        store: stores?.length || 0,
    };

    const points = useMemo(() => {
        const out = [];
        const push = (items, kind) => {
            (items || []).forEach((it) => {
                const ll = parseLatLng(it.location);
                if (ll) out.push({ kind, position: ll, item: it });
            });
        };
        push(businesses, 'business');
        push(events, 'event');
        push(barters, 'barter');
        // Stores ride on their business location
        push((stores || []).map(s => s._business).filter(Boolean), 'store');
        return out;
    }, [businesses, events, barters, stores]);

    const filteredPoints = points.filter(p => visible[p.kind]);
    const bounds = filteredPoints.map(p => p.position);

    // The map mounts once, up front, so Leaflet + tiles initialise during the
    // page load rather than mid-interaction (the old gradient→map remount was
    // the "freeze"). It stays hidden under a gradient veil until FitToPoints
    // has framed the zone, then crossfades in already-perfect.
    const [mapRevealed, setMapRevealed] = useState(false);
    const revealMap = React.useCallback(() => {
        // Give the freshly-framed tiles a beat to paint before the crossfade,
        // so we fade onto a finished map, not gray tile placeholders.
        setTimeout(() => setMapRevealed(true), 280);
    }, []);

    const firstOtherZone = otherZones && otherZones.length > 0
        ? [otherZones[0].lat, otherZones[0].lng]
        : null;
    const center = bounds[0] || zoneCenter || firstOtherZone || [32.08, 34.78];

    const FILTERS = [
        { kind: 'business', label: 'עסקים', icon: Store, count: counts.business },
        { kind: 'event', label: 'מפגשים', icon: CalendarDays, count: counts.event },
        { kind: 'store', label: 'חנויות', icon: ShoppingBag, count: counts.store },
        { kind: 'barter', label: 'בארטרים', icon: Handshake, count: counts.barter },
    ];

    const someHidden = FILTERS.some(f => f.count > 0 && !visible[f.kind]);
    const allHideable = FILTERS.some(f => f.count > 0 && visible[f.kind]);

    // No businesses, events, barters or stores anywhere in this zone — the
    // community has nothing on the map yet. Instead of an empty map, greet
    // visitors with an invitation to be the first to bring it to life. Held
    // back until loading finishes so the banner never flashes mid-fetch.
    const totalEntities = counts.business + counts.event + counts.barter + counts.store;
    const showEmptyState = !loading && totalEntities === 0;

    // In full-screen mode the map sits under the app's sticky top navbar (h-14),
    // so push the floating chrome down clear of it.
    const topChromeClass = fullScreen
        ? 'top-[4.25rem] sm:top-[4.75rem]'
        : 'top-4 sm:top-6';

    return (
        <header
            className="relative w-full overflow-hidden"
            style={{ height: fullScreen ? '100%' : 'clamp(360px, 52vh, 520px)' }}
        >
            {/* Map layer — mounted once, immediately, so Leaflet + tiles
                initialise during page load instead of mid-interaction. It
                lives under the gradient veil until the zone is framed. */}
            <MapContainer
                center={center}
                zoom={14}
                zoomControl={false}
                scrollWheelZoom={fullScreen}
                dragging={true}
                style={{ height: '100%', width: '100%' }}
                className="absolute inset-0 z-0"
            >
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    attribution="&copy; OpenStreetMap &copy; CARTO"
                />
                <FitToPoints
                    points={bounds}
                    zoneCenter={zoneCenter}
                    fallbackPoints={(otherZones || []).map(z => [z.lat, z.lng])}
                    onFramed={revealMap}
                />
                {filteredPoints.map((p, i) => {
                    const name = p.item.business_name || p.item.title || p.item.name;
                    const cover = p.item.cover_image || p.item.image || p.item.image_key;
                    return (
                        <Marker
                            key={`${p.kind}-${i}`}
                            position={p.position}
                            icon={PINS[p.kind]}
                        >
                            <Popup className="custom-popup" maxWidth={160}>
                                <div
                                    className="relative w-40 h-40 cursor-pointer overflow-hidden"
                                    onClick={() => onPinClick && onPinClick(p.item, p.kind)}
                                >
                                    {cover ? (
                                        <img
                                            src={cover}
                                            alt={name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div
                                            className="w-full h-full flex items-center justify-center"
                                            style={{ background: PIN_PALETTE[p.kind].bg }}
                                        >
                                            {React.cloneElement(PIN_PALETTE[p.kind].icon, { size: 40 })}
                                        </div>
                                    )}
                                    <div className="absolute bottom-2 left-2 right-2 flex justify-center">
                                        <span className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full font-bold text-xs text-ink-strong shadow-lg text-center">
                                            {name}
                                        </span>
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}

                {/* Every other zone in the network — clustered so zooming out
                        from this zone lets the user spot and jump to a neighbour. */}
                <ClusteredZoneMarkers zones={otherZones} />
            </MapContainer>

            {/* Gradient veil — covers the map while it initialises and frames
                the zone off-screen, then gracefully crossfades away so the
                map only ever appears already-perfect. Stays put if we never
                get coordinates, doubling as the poetic empty state. */}
            <div
                aria-hidden="true"
                className="absolute inset-0 z-[1] pointer-events-none transition-opacity duration-700 ease-out"
                style={{
                    opacity: mapRevealed ? 0 : 1,
                    background:
                        'radial-gradient(at 70% 30%, rgba(201,166,107,0.35), transparent 60%), ' +
                        'radial-gradient(at 20% 80%, rgba(139,154,107,0.45), transparent 60%), ' +
                        'linear-gradient(180deg, #f5f7ef 0%, #e9eedc 100%)',
                }}
            />

            {/* Soft gradient overlays so chrome stays legible over any map tile */}
            <div
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-40 z-10 pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(245,247,239,0.92) 0%, rgba(245,247,239,0) 100%)' }}
            />
            <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-24 z-10 pointer-events-none"
                style={{ background: 'linear-gradient(0deg, rgba(245,247,239,0.92) 0%, rgba(245,247,239,0) 100%)' }}
            />

            {/* Poetic empty state — no entities anywhere in this zone yet. */}
            {showEmptyState && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 z-20 flex items-center justify-center px-6 pointer-events-none"
                >
                    <div className="max-w-sm text-center bg-white/80 backdrop-blur-xl rounded-3xl ring-1 ring-line-subtle shadow-elevated px-7 py-8">
                        <span className="relative mx-auto mb-4 inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-ink-inverse shadow-md ring-2 ring-white/90">
                            <Sparkles className="w-8 h-8" strokeWidth={2} />
                            <motion.span
                                aria-hidden="true"
                                className="absolute inset-0 rounded-2xl ring-2 ring-brand-400/60"
                                animate={{ scale: [1, 1.25, 1], opacity: [0.6, 0, 0.6] }}
                                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                            />
                        </span>
                        <h2 className="heading-lg text-ink mb-2">הקהילה הזו מחכה להיוולד</h2>
                        <p className="text-sm text-ink-muted leading-relaxed">
                            כאן עוד שקט - אבל לא לאורך זמן. היו הראשונים להוסיף עסק, מפגש או יוזמה, ולהצית את הניצוץ הראשון
                        </p>
                    </div>
                </motion.div>
            )}

            {/* Identity — sits top-right (RTL leading edge) */}
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className={clsx('absolute right-4 sm:right-6 z-20 max-w-[78%]', topChromeClass)}
            >
                <div className="inline-flex items-center gap-3">
                    <span className="relative inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-ink-inverse shadow-md ring-2 ring-white/90 shrink-0">
                        <MapPin className="w-6 h-6" strokeWidth={2} />
                    </span>
                    <div className="min-w-0">
                        <p className="eyebrow text-brand-700 mb-0.5">קהילה</p>
                        <h1 className="heading-xl sm:display-2 text-ink leading-tight truncate drop-shadow-sm">
                            {zoneName}
                        </h1>
                        {memberCount > 0 && (
                            <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted">
                                <Users className="w-4 h-4" strokeWidth={2} />
                                {memberCount.toLocaleString('he-IL')} חברי קהילה
                            </p>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Actions — top-left (RTL trailing edge) */}
            <div className={clsx('absolute left-4 sm:left-6 z-20 flex items-start gap-2', topChromeClass)}>
                <IconButton
                    icon={<MessageCircle />}
                    aria-label="צ׳אט קהילתי"
                    variant="solid"
                    tone="brand"
                    shape="round"
                    onClick={onChatClick}
                />
                <IconButton
                    icon={<Share2 />}
                    aria-label="שתפו את הקהילה"
                    variant="soft"
                    tone="brand"
                    shape="round"
                    onClick={onShareClick}
                />

                {/* Map Layers — trigger + popover */}
                <div ref={layersRef} className="relative">
                    <div className="relative">
                        <IconButton
                            icon={<Layers />}
                            aria-label="שכבות מפה"
                            aria-expanded={layersOpen}
                            aria-haspopup="dialog"
                            variant={layersOpen ? 'solid' : 'soft'}
                            tone="brand"
                            shape="round"
                            onClick={() => setLayersOpen(o => !o)}
                        />
                        {someHidden && !layersOpen && (
                            <span
                                aria-hidden="true"
                                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-brand-500 ring-2 ring-white"
                            />
                        )}
                    </div>

                    {createPortal(
                        <AnimatePresence>
                            {layersOpen && (
                                <motion.div
                                    ref={popoverRef}
                                    role="dialog"
                                    aria-label="שכבות מפה"
                                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                                    style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left }}
                                    className="w-64 origin-top-left z-modal"
                                >
                                    <div className="bg-white/95 backdrop-blur-md rounded-2xl ring-1 ring-line-subtle shadow-elevated overflow-hidden">
                                        {/* Header */}
                                        <div className="flex items-center justify-between gap-3 px-3 pt-3 pb-2">
                                            <div className="min-w-0">
                                                <p className="eyebrow text-brand-700 mb-0.5">שכבות מפה</p>
                                                <h3 className="text-sm font-bold text-ink leading-none">מציג במפה</h3>
                                            </div>
                                            {someHidden ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setVisible({ business: true, event: true, barter: true, store: true })}
                                                    className="text-2xs font-bold text-brand-700 hover:text-brand-800 px-2 py-1 rounded-md hover:bg-brand-50 transition-colors"
                                                >
                                                    הצג הכל
                                                </button>
                                            ) : allHideable && (
                                                <button
                                                    type="button"
                                                    onClick={() => setVisible({ business: false, event: false, barter: false, store: false })}
                                                    className="text-2xs font-bold text-ink-muted hover:text-ink px-2 py-1 rounded-md hover:bg-surface-muted transition-colors"
                                                >
                                                    הסתר הכל
                                                </button>
                                            )}
                                        </div>

                                        <div className="h-px bg-line-subtle/70 mx-3" />

                                        {/* Rows */}
                                        <div className="flex flex-col p-1.5">
                                            {FILTERS.map(({ kind, label, icon: Icon, count }) => {
                                                const active = visible[kind];
                                                const empty = count === 0;
                                                return (
                                                    <button
                                                        key={kind}
                                                        type="button"
                                                        onClick={() => !empty && setVisible(v => ({ ...v, [kind]: !v[kind] }))}
                                                        disabled={empty}
                                                        aria-pressed={active}
                                                        className={clsx(
                                                            'flex items-center gap-2.5 px-2 py-2 rounded-xl text-sm font-semibold w-full text-right',
                                                            'transition-colors duration-150',
                                                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                                                            empty && 'opacity-40 cursor-not-allowed',
                                                            !empty && 'hover:bg-surface-muted/70 active:bg-surface-muted',
                                                            !empty && active && 'text-ink',
                                                            !empty && !active && 'text-ink-muted',
                                                        )}
                                                    >
                                                        <span
                                                            aria-hidden="true"
                                                            className={clsx(
                                                                'inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 transition-all duration-200',
                                                                active
                                                                    ? 'ring-2 ring-white shadow-card'
                                                                    : 'opacity-40 grayscale ring-1 ring-line-subtle',
                                                            )}
                                                            style={{ background: PIN_PALETTE[kind].bg, color: 'white' }}
                                                        >
                                                            <Icon size={14} strokeWidth={2.5} />
                                                        </span>
                                                        <span className="flex-1 text-right">{label}</span>
                                                        <span
                                                            className={clsx(
                                                                'inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-pill text-2xs font-bold',
                                                                active ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100' : 'bg-surface-muted text-ink-faint',
                                                            )}
                                                        >
                                                            {count}
                                                        </span>
                                                        {active ? (
                                                            <Eye size={16} className="text-brand-600 shrink-0" strokeWidth={2.25} />
                                                        ) : (
                                                            <EyeOff size={16} className="text-ink-faint shrink-0" strokeWidth={2.25} />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>,
                        document.body
                    )}
                </div>
            </div>

            {/* Subtle "scroll hint" sparkle — invites the eye downward.
                Hidden in full-screen mode (no scroll affordance there). */}
            {!fullScreen && (
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.4 }}
                        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 pointer-events-none text-ink-faint"
                    >
                        <Sparkles className="w-4 h-4" />
                    </motion.div>
                </AnimatePresence>
            )}
        </header>
    );
}

export default ZoneMapHero;
